import React from 'react';
import { useTranslation } from 'react-i18next';
import { AngleDownIcon, AngleRightIcon } from '@patternfly/react-icons';
import './styles.scss';

export type LabCardProps = {
  children: React.ReactNode;
  className?: string;
};

export const LabCard = ({ children, className = '' }: LabCardProps) => (
  <article className={`lab-card ${className}`.trim()}>{children}</article>
);

type LabCardHeaderProps = {
  icon?: React.ReactNode;
  title: React.ReactNode;
  trailing?: React.ReactNode;
  isExpanded?: boolean;
  onToggle?: () => void;
};

export const LabCardHeader = ({
  icon,
  title,
  trailing,
  isExpanded = true,
  onToggle,
}: LabCardHeaderProps) => {
  const { t } = useTranslation();

  return (
    <header className="lab-card__header">
      <div className="lab-card__header-inner">
        {icon ? <span className="lab-card__header-icon">{icon}</span> : null}
        <span className="lab-card__header-title">{title}</span>
        {trailing ? (
          <div
            className="lab-card__header-trailing"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            role="presentation"
          >
            {trailing}
          </div>
        ) : null}
        {onToggle ? (
          <button
            type="button"
            className="lab-card__header-expand"
            onClick={onToggle}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? t('Collapse') : t('Expand')}
          >
            <span className="lab-card__header-chevron" aria-hidden="true">
              {isExpanded ? <AngleDownIcon /> : <AngleRightIcon />}
            </span>
          </button>
        ) : null}
      </div>
    </header>
  );
};

type LabCardRowProps = {
  label: string;
  actionLabel?: string;
  onActionClick?: () => void;
  actionDisabled?: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
  analyticsId?: string;
};

export const LabCardRow = ({
  label,
  actionLabel,
  onActionClick,
  actionDisabled = false,
  isExpanded,
  onToggle,
  children,
  analyticsId,
}: LabCardRowProps) => (
  <div className="lab-card__row">
    <div className="lab-card__row-header">
      <button
        type="button"
        className="lab-card__row-toggle"
        onClick={onToggle}
        aria-expanded={isExpanded}
        data-analytics-id={analyticsId}
      >
        <span className="lab-card__row-label">{label}</span>
      </button>
      <span className="lab-card__row-actions">
        {actionLabel ? (
          <button
            type="button"
            className={`lab-card__row-action${
              actionDisabled ? ' lab-card__row-action--disabled' : ''
            }`}
            disabled={actionDisabled}
            onClick={onActionClick}
          >
            {actionLabel}
          </button>
        ) : null}
        <button
          type="button"
          className="lab-card__row-chevron-btn"
          onClick={onToggle}
          tabIndex={-1}
          aria-hidden="true"
        >
          <span className="lab-card__row-chevron">
            {isExpanded ? <AngleDownIcon /> : <AngleRightIcon />}
          </span>
        </button>
      </span>
    </div>
    {isExpanded && children ? (
      <div className="lab-card__row-body">{children}</div>
    ) : null}
  </div>
);

export const LabCardBody = ({ children }: { children: React.ReactNode }) => (
  <div className="lab-card__body">{children}</div>
);

export const LabCardBanner = ({ children }: { children: React.ReactNode }) => (
  <div className="lab-card__banner">{children}</div>
);

export const LabCardResources = ({
  children,
}: {
  children: React.ReactNode;
}) => <div className="lab-card__resources">{children}</div>;

export const LabCardAutoMgmt = ({
  children,
}: {
  children: React.ReactNode;
}) => <div className="lab-card__auto-mgmt">{children}</div>;

export const LabCardFooter = ({ children }: { children: React.ReactNode }) => (
  <footer className="lab-card__footer">{children}</footer>
);
